import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import styled, { css } from 'styled-components/macro';
import { WxLabel, WxTitle } from '../components';
import { DesktopOnly } from '../components/layout/nav/DesktopNavbar';
import { MobileOnly } from '../components/layout/nav/Nav';
import { CategoryBreadCrumb } from '../components/pages/category/BreadCrumb';
import { Products } from '../components/pages/category/Products';
import { Collections } from '../components/sections/collection/Collections';
import { SubCategoryFilter } from '../components/ui/category/SubCategoryFilter';
import { FilterCards } from '../components/ui/filters/FilterCards';
import { FiltersMenu } from '../components/ui/filters/FiltersMenu';
import useCategory from '../hooks/api/queries/useCategory';
import useMenu from '../hooks/api/queries/useMenu';
import {
  initialFilters,
  useFiltersContext,
} from '../hooks/context/useFiltersContext';
import useScrollLock from '../hooks/context/useScrollLock';
import { __ } from '../i18n/l10n';
import { breakpoint } from '../styles/constants';
import { appendCommitHash } from '../utils/appendHash';
import { categoryDepth } from '../utils/categoryDepth';
import { NotFound } from './404';

export const Category = () => {
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
  const { filters, setSubCatList, setCurrentCat } = useFiltersContext();
  const { lockScroll, unlockScroll } = useScrollLock();
  const location = useLocation();
  const { categorySlug } = useParams();
  const navigate = useNavigate();

  const { data: menu } = useMenu();
  const { depth, category } = categoryDepth({ menu, categorySlug });
  const productPage = depth > 1 || (category?.children?.length ?? 0) === 0;

  useEffect(() => {
    setFiltersOpen(false);
    if (!productPage) {
      setSubCatList([]);
    }
  }, [location.pathname, productPage, setSubCatList]);

  const filter = {
    categorySlug,
  };

  const { data, isLoading } = useCategory(filter);

  useEffect(() => {
    filtersOpen && lockScroll();
    !filtersOpen && unlockScroll();
  }, [filtersOpen, lockScroll, unlockScroll]);

  useEffect(() => {
    setCurrentCat(categorySlug);
  }, [categorySlug, setCurrentCat]);

  if (!categorySlug) {
    navigate(appendCommitHash('/'));
    return null;
  }
  if (!data || (data as any).rows?.length === 0 || !menu) {
    if (!isLoading) return <NotFound />;
    else return null;
  }

  return (
    <PageWrapper>
      {filtersOpen && productPage && (
        <FiltersMenu setOpen={setFiltersOpen} categoryId={data.id} />
      )}
      <CategoryBreadCrumb categorySlug={categorySlug} />
      <Row>
        <WxTitle title={data.name} align={'left'} size={'big'} darkMode />
        {productPage && [0, null, undefined].includes(data.hideFilters) && (
          <MobileOnly>
            <ButtonFilter onClick={() => setFiltersOpen(!filtersOpen)}>
              <WxLabel
                label={__('Filters')}
                active={!_.isEqual(filters, initialFilters)}
              />
            </ButtonFilter>
          </MobileOnly>
        )}
      </Row>
      <Row justifyEnd={(category?.children?.length ?? 0) === 0 && productPage}>
        {depth === 2 && (category?.children?.length ?? 0) > 0 && (
          <SubCategoryFilter category={data} />
        )}
        {productPage && [0, null, undefined].includes(data.hideFilters) && (
          <DesktopOnly>
            <ButtonFilter onClick={() => setFiltersOpen(!filtersOpen)}>
              <WxLabel
                label={__('Filters')}
                active={!_.isEqual(filters, initialFilters)}
              />
            </ButtonFilter>
          </DesktopOnly>
        )}
      </Row>
      <FilterCards desktop />

      {productPage ? (
        <Products categoryId={data.id} categorySlug={categorySlug} />
      ) : (
        <Collections categoryCards={data.children} />
      )}
    </PageWrapper>
  );
};

export const PageWrapper = styled.div<{ backgroundColor?: string }>`
  height: 100%;
  display: flex;
  user-select: none;
  flex-direction: column;
  flex: 1;
  padding-bottom: 3.75rem;

  ${({ backgroundColor }) =>
    backgroundColor &&
    css`
      box-shadow: 0 0 0 100vmax ${backgroundColor};
      clip-path: inset(0 -100vmax);
      background-color: ${backgroundColor};
    `}
`;

const Row = styled.div<{ justifyEnd?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  margin-top: 0.625rem;

  @media (min-width: ${breakpoint}) {
    align-items: center;
    justify-content: ${({ justifyEnd }) =>
      justifyEnd ? 'flex-end' : 'space-between'};
    width: 100%;
  }
`;

const ButtonFilter = styled.button`
  background-color: transparent;
  border: unset;
  margin-top: auto;

  p {
    font-size: 0.875rem;
  }

  @media (min-width: ${breakpoint}) {
    margin-top: 1.875rem;
    margin-left: auto;
  }
`;
